import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Loader2,
  PanelLeftClose,
  PanelLeft,
  Download,
  RefreshCw,
  EyeOff,
  Share2,
  PenLine,
  ListChecks
} from 'lucide-react';

// Components
import Sidebar from '../components/sidebar/Sidebar';
import MessageList from '../components/chat/MessageList';
import MessageInput from '../components/chat/MessageInput';
import EmptyState from '../components/chat/EmptyState';
import MemoriesModal from '../components/modals/MemoriesModal';
import KnowledgeBaseModal from '../components/modals/KnowledgeBaseModal';
import ShareModal from '../components/modals/ShareModal';
import ProfileModal from '../components/modals/ProfileModal';
import DraftModal from '../components/modals/DraftModal';
import FocusModal from '../components/modals/FocusModal';
import ActionsPanel from '../components/panels/ActionsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  // Core state
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('general');
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Chat editing state
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatTitle, setEditingChatTitle] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [draggedChat, setDraggedChat] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [chatMenuId, setChatMenuId] = useState(null);

  // Memory state
  const [showMemories, setShowMemories] = useState(false);
  const [memories, setMemories] = useState([]);
  const [newMemory, setNewMemory] = useState('');
  const [newMemoryCategory, setNewMemoryCategory] = useState('general');
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memorySuggestions, setMemorySuggestions] = useState([]);

  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  // Document state
  const [showDocuments, setShowDocuments] = useState(false);
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageLimit, setStorageLimit] = useState(100 * 1024 * 1024);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Chat attachments
  const [chatAttachments, setChatAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Settings menu
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // Search
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Temporary chat mode
  const [isTemporaryMode, setIsTemporaryMode] = useState(false);
  const [temporaryMessages, setTemporaryMessages] = useState([]);

  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);

  // Profile modal
  const [showProfile, setShowProfile] = useState(false);

  // Draft modal
  const [showDraftModal, setShowDraftModal] = useState(false);

  // Actions panel
  const [showActionsPanel, setShowActionsPanel] = useState(false);

  // Focus modal
  const [showFocus, setShowFocus] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const { user, logout, updateUser } = useAuth();

  // Search conversations
  const searchConversations = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/search`, { params: { q: query, limit: 20 } });
      setSearchResults(response.data.results || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchConversations(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchConversations]);

  // Export conversation
  const exportConversation = () => {
    if (messages.length === 0) {
      toast.error('No messages to export');
      return;
    }
  
    const conversationTitle = currentConversation?.title || 'Arlo Conversation';
    const date = new Date().toLocaleDateString();
  
    let content = `# ${conversationTitle}\n`;
    content += `Exported on ${date}\n\n`;
    content += `---\n\n`;
  
    messages.forEach(msg => {
      const role = msg.role === 'user' ? 'You' : 'Arlo';
      const time = new Date(msg.created_at).toLocaleTimeString();
      content += `**${role}** (${time}):\n${msg.content}\n\n`;
    });
  
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${conversationTitle.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Conversation exported');
  };

  // Load memories
  const loadMemories = useCallback(async () => {
    try {
      setMemoriesLoading(true);
      const response = await axios.get(`${API}/memories`);
      setMemories(response.data.memories || []);
    } catch (err) {
      console.error('Failed to load memories:', err);
    } finally {
      setMemoriesLoading(false);
    }
  }, []);

  // Load knowledge documents
  const loadKnowledgeDocs = useCallback(async () => {
    try {
      setDocsLoading(true);
      const response = await axios.get(`${API}/knowledge-docs`);
      setKnowledgeDocs(response.data.documents || []);
      setStorageUsed(response.data.storage_used || 0);
      setStorageLimit(response.data.storage_limit || 100 * 1024 * 1024);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  // Load chat attachments
  const loadChatAttachments = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const response = await axios.get(`${API}/chat/attachments/${convId}`);
      setChatAttachments(response.data.attachments || []);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  }, []);

  // Load folders
  const loadFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/folders`);
      setFolders(response.data.folders || []);
    } catch (err) {
      console.error('Failed to load folders:', err);
    }
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, []);

  // Load chat history
  const loadHistory = useCallback(async (conversationId = null) => {
    try {
      const url = conversationId 
        ? `${API}/chat/history?conversation_id=${conversationId}`
        : `${API}/chat/history`;
      const response = await axios.get(url);
      setMessages(response.data.messages || []);
      // Get conversation_id from conversation object if not at top level
      const convId = response.data.conversation_id || response.data.conversation?.id;
      setCurrentConversationId(convId);
      setCurrentConversation(response.data.conversation);
      if (convId) {
        loadChatAttachments(convId);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setHistoryLoading(false);
    }
  }, [logout, loadChatAttachments]);

  useEffect(() => {
    loadHistory();
    loadConversations();
    loadFolders();
    loadMemories();
    loadKnowledgeDocs();
  }, [loadHistory, loadConversations, loadFolders, loadMemories, loadKnowledgeDocs]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  useEffect(() => {
    if (!loading && !streaming) {
      textareaRef.current?.focus();
    }
  }, [loading, streaming, messages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showModeSelector && !e.target.closest('[data-mode-selector]')) {
        setShowModeSelector(false);
      }
      if (chatMenuId && !e.target.closest('[data-chat-menu]')) {
        setChatMenuId(null);
      }
      if (showSettingsMenu && !e.target.closest('[data-settings-menu]')) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showModeSelector, chatMenuId, showSettingsMenu]);

  // Format text for copying
  const formatForCopy = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^#+\s/gm, '')
      .replace(/^[-*]\s/gm, '- ')
      .trim();
  };

  const copyToClipboard = async (text, messageId) => {
    try {
      const cleanText = formatForCopy(text);
      await navigator.clipboard.writeText(cleanText);
      setCopiedId(messageId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Folder operations
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const response = await axios.post(`${API}/folders`, { name: newFolderName.trim() });
      setFolders(prev => [...prev, response.data.folder]);
      setNewFolderName('');
      setShowNewFolder(false);
      setExpandedFolders(prev => ({ ...prev, [response.data.folder.id]: true }));
      toast.success('Folder created');
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const renameFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      await axios.patch(`${API}/folders/${folderId}`, { name: editingFolderName.trim() });
      setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: editingFolderName.trim() } : f));
      setEditingFolderId(null);
      setEditingFolderName('');
    } catch (err) {
      toast.error('Failed to rename folder');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      setFolders(prev => prev.filter(f => f.id !== folderId));
      loadConversations();
      toast.success('Folder deleted');
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  // Chat operations
  const createNewConversation = async (folderId = null) => {
    try {
      const response = await axios.post(`${API}/conversations`, { folder_id: folderId });
      const newConv = response.data.conversation;
      setConversations(prev => [newConv, ...prev]);
      setCurrentConversationId(newConv.id);
      setCurrentConversation(newConv);
      setMessages([]);
      setShowSidebar(false);
      setIsTemporaryMode(false);
      setTemporaryMessages([]);
      toast.success('New chat created');
    } catch (err) {
      toast.error('Failed to create chat');
    }
  };

  const startTemporaryChat = () => {
    setIsTemporaryMode(true);
    setTemporaryMessages([]);
    setCurrentConversationId(null);
    setCurrentConversation(null);
    setMessages([]);
    setShowSidebar(false);
    toast.success('Temporary chat started - messages will not be saved');
  };

  const endTemporaryChat = () => {
    setIsTemporaryMode(false);
    setTemporaryMessages([]);
  };

  const renameChat = async (chatId) => {
    if (!editingChatTitle.trim()) return;
    try {
      await axios.patch(`${API}/conversations/${chatId}`, { title: editingChatTitle.trim() });
      setConversations(prev => prev.map(c => c.id === chatId ? { ...c, title: editingChatTitle.trim() } : c));
      if (currentConversationId === chatId) {
        setCurrentConversation(prev => prev ? { ...prev, title: editingChatTitle.trim() } : null);
      }
      setEditingChatId(null);
      setEditingChatTitle('');
    } catch (err) {
      toast.error('Failed to rename chat');
    }
  };

  const moveChat = async (chatId, folderId) => {
    try {
      await axios.patch(`${API}/conversations/${chatId}`, { folder_id: folderId || '' });
      setConversations(prev => prev.map(c => c.id === chatId ? { ...c, folder_id: folderId || null } : c));
      toast.success('Chat moved');
    } catch (err) {
      toast.error('Failed to move chat');
    }
  };

  const switchConversation = async (conversationId) => {
    setHistoryLoading(true);
    await loadHistory(conversationId);
    setShowSidebar(false);
  };

  const deleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${API}/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        const remaining = conversations.filter(c => c.id !== conversationId);
        if (remaining.length > 0) {
          await switchConversation(remaining[0].id);
        } else {
          await createNewConversation();
        }
      }
      toast.success('Chat deleted');
    } catch (err) {
      toast.error('Failed to delete chat');
    }
  };

  // Memory operations
  const addMemory = async () => {
    if (!newMemory.trim()) return;
    try {
      const response = await axios.post(`${API}/memories`, {
        content: newMemory.trim(),
        category: newMemoryCategory
      });
      setMemories(prev => [response.data.memory, ...prev]);
      setNewMemory('');
      toast.success('Memory saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save memory');
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      await axios.delete(`${API}/memories/${memoryId}`);
      setMemories(prev => prev.filter(m => m.id !== memoryId));
      toast.success('Memory deleted');
    } catch (err) {
      toast.error('Failed to delete memory');
    }
  };

  const saveMemorySuggestion = async (suggestion) => {
    try {
      const response = await axios.post(`${API}/memories`, {
        content: suggestion.content,
        category: suggestion.category
      });
      setMemories(prev => [response.data.memory, ...prev]);
      setMemorySuggestions(prev => prev.filter(s => s.content !== suggestion.content));
      toast.success('Memory saved');
    } catch (err) {
      toast.error('Failed to save memory');
    }
  };

  const dismissMemorySuggestion = (suggestion) => {
    setMemorySuggestions(prev => prev.filter(s => s.content !== suggestion.content));
  };

  // Update memory
  const updateMemory = async (memoryId, content, category) => {
    try {
      await axios.patch(`${API}/memories/${memoryId}`, { content, category });
      setMemories(prev => prev.map(m => 
        m.id === memoryId ? { ...m, content, category } : m
      ));
      toast.success('Memory updated');
    } catch (err) {
      toast.error('Failed to update memory');
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        // Clear recording interval
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }
        setRecordingDuration(0);
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
    
      // Start duration timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    
      toast.success('Recording started - speak now');
    } catch (err) {
      console.error('Microphone access denied:', err);
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
    
      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    
      if (response.data.text) {
        setInput(prev => prev + (prev ? ' ' : '') + response.data.text);
        textareaRef.current?.focus();
        toast.success('Transcription complete');
      }
    } catch (err) {
      console.error('Transcription failed:', err);
      toast.error('Failed to transcribe audio');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Knowledge document operations
  const uploadKnowledgeDoc = async (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
    
      const response = await axios.post(`${API}/knowledge-docs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    
      setKnowledgeDocs(prev => [response.data.document, ...prev]);
      setStorageUsed(prev => prev + (response.data.document.file_size || 0));
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteKnowledgeDoc = async (docId) => {
    const doc = knowledgeDocs.find(d => d.id === docId);
    try {
      await axios.delete(`${API}/knowledge-docs/${docId}`);
      setKnowledgeDocs(prev => prev.filter(d => d.id !== docId));
      setStorageUsed(prev => prev - (doc?.file_size || 0));
      toast.success('Document deleted');
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  // Chat attachment operations
  const uploadChatAttachment = async (file) => {
    if (!currentConversationId) {
      toast.error('Please start a conversation first');
      return;
    }

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', currentConversationId);
    
      const response = await axios.post(`${API}/chat/attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    
      setChatAttachments(prev => [response.data.attachment, ...prev]);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Drag and drop
  const handleDragStart = (e, chatId) => {
    setDraggedChat(chatId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(targetId);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    if (draggedChat) {
      await moveChat(draggedChat, folderId);
    }
    setDraggedChat(null);
    setDropTarget(null);
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading || streaming) return;

    const content = input.trim();
    setInput('');
    setError(null);
    setLoading(true);

    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString()
    };
  
    // Handle temporary mode differently
    if (isTemporaryMode) {
      setTemporaryMessages(prev => [...prev, tempUserMsg]);
    
      try {
        setStreaming(true);
        setStreamingContent('');

        // Build conversation history for temporary mode
        const conversationHistory = temporaryMessages.map(m => ({
          role: m.role,
          content: m.content
        }));

        const response = await fetch(`${API}/chat/temporary/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            content, 
            mode, 
            conversation_history: conversationHistory 
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to send message');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
              
                if (data.type === 'chunk') {
                  fullContent += data.content;
                  setStreamingContent(fullContent);
                } else if (data.type === 'done') {
                  setTemporaryMessages(prev => [...prev, {
                    id: `temp-assistant-${Date.now()}`,
                    role: 'assistant',
                    content: data.full_content,
                    created_at: new Date().toISOString()
                  }]);
                  setStreamingContent('');
                  // No memory suggestions in temporary mode
                } else if (data.type === 'error') {
                  throw new Error(data.message);
                }
              } catch (e) {
                if (e.message !== 'Unexpected end of JSON input') {
                  console.error('Parse error:', e);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Temporary chat error:', err);
        setError(err.message || 'Something went wrong. Please try again.');
        setTemporaryMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
        setInput(content);
        toast.error(err.message || 'Failed to send message');
      } finally {
        setLoading(false);
        setStreaming(false);
        setStreamingContent('');
      }
      return;
    }
  
    // Regular chat mode
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      setStreaming(true);
      setStreamingContent('');

      const response = await fetch(`${API}/chat/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content, mode, conversation_id: currentConversationId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let assistantMsgId = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
            
              if (data.type === 'start') {
                assistantMsgId = data.assistant_message_id;
                setMessages(prev => prev.map(msg => 
                  msg.id === tempUserMsg.id ? { ...msg, id: data.user_message_id } : msg
                ));
              } else if (data.type === 'chunk') {
                fullContent += data.content;
                setStreamingContent(fullContent);
              } else if (data.type === 'done') {
                setMessages(prev => [...prev, {
                  id: assistantMsgId,
                  role: 'assistant',
                  content: data.full_content,
                  created_at: new Date().toISOString()
                }]);
                setStreamingContent('');
                
                // Update conversation title immediately if auto-named
                if (data.new_title && currentConversationId) {
                  setConversations(prev => prev.map(c => 
                    c.id === currentConversationId ? { ...c, title: data.new_title } : c
                  ));
                  setCurrentConversation(prev => prev ? { ...prev, title: data.new_title } : prev);
                }
                
                loadConversations();
                if (data.memory_suggestions && data.memory_suggestions.length > 0) {
                  setMemorySuggestions(data.memory_suggestions);
                }
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              if (e.message !== 'Unexpected end of JSON input') {
                console.error('Parse error:', e);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Send message error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setInput(content);
      toast.error(err.message || 'Failed to send message');
    } finally {
      setLoading(false);
      setStreaming(false);
      setStreamingContent('');
    }
  }, [input, loading, streaming, mode, currentConversationId, loadConversations, isTemporaryMode, temporaryMessages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleRetry = () => {
    setError(null);
    if (input.trim()) sendMessage();
  };

  const handleLogout = () => {
    logout();
    toast.success('Signed out');
  };

  const formatMessageContent = (content) => {
    return content.split('\n').map((line, i) => {
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      line = line.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>');
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
      }
      const numberedMatch = line.match(/^(\d+)\.\s/);
      if (numberedMatch) {
        return <li key={i} className="ml-4 list-decimal" dangerouslySetInnerHTML={{ __html: line.slice(numberedMatch[0].length) }} />;
      }
      return <p key={i} dangerouslySetInnerHTML={{ __html: line || '&nbsp;' }} />;
    });
  };

  if (historyLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="chat-page">
      {/* Sidebar */}
      <Sidebar
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        conversations={conversations}
        folders={folders}
        currentConversationId={currentConversationId}
        expandedFolders={expandedFolders}
        setExpandedFolders={setExpandedFolders}
        editingChatId={editingChatId}
        setEditingChatId={setEditingChatId}
        editingChatTitle={editingChatTitle}
        setEditingChatTitle={setEditingChatTitle}
        editingFolderId={editingFolderId}
        setEditingFolderId={setEditingFolderId}
        editingFolderName={editingFolderName}
        setEditingFolderName={setEditingFolderName}
        newFolderName={newFolderName}
        setNewFolderName={setNewFolderName}
        showNewFolder={showNewFolder}
        setShowNewFolder={setShowNewFolder}
        chatMenuId={chatMenuId}
        setChatMenuId={setChatMenuId}
        draggedChat={draggedChat}
        dropTarget={dropTarget}
        showSettingsMenu={showSettingsMenu}
        setShowSettingsMenu={setShowSettingsMenu}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResults={searchResults}
        searching={searching}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        isTemporaryMode={isTemporaryMode}
        switchConversation={switchConversation}
        createNewConversation={createNewConversation}
        startTemporaryChat={startTemporaryChat}
        renameChat={renameChat}
        deleteConversation={deleteConversation}
        createFolder={createFolder}
        renameFolder={renameFolder}
        deleteFolder={deleteFolder}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        handleLogout={handleLogout}
        setShowMemories={setShowMemories}
        setShowDocuments={setShowDocuments}
        setShowProfile={setShowProfile}
        setShowFocus={setShowFocus}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border px-4 sm:px-6 py-3">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setShowSidebar(!showSidebar)} className="h-9 w-9 p-0" data-testid="sidebar-toggle">
                {showSidebar ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
              </Button>
              <div className="relative inline-block">
                <div className="absolute -top-0.5 left-0">
                  <div className="bg-white text-background px-0.5 py-0.5 rounded text-[6px] font-bold leading-none" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>hey</div>
                  <div className="absolute -bottom-0.5 left-1 w-1 h-1 bg-white transform rotate-45"></div>
                </div>
                <span className="text-[28px] font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
              </div>
            </div>
            {messages.length > 0 && !isTemporaryMode && (
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowDraftModal(true)}
                  className="text-muted-foreground hover:text-foreground"
                  title="Generate smart draft"
                  data-testid="draft-btn"
                >
                  <PenLine className="h-4 w-4 mr-2" /><span className="hidden sm:inline">Draft</span>
                </Button>
                <Button 
                  variant={showActionsPanel ? "default" : "ghost"}
                  size="sm" 
                  onClick={() => setShowActionsPanel(!showActionsPanel)}
                  className={showActionsPanel ? "" : "text-muted-foreground hover:text-foreground"}
                  title="Extract action items"
                  data-testid="actions-btn"
                >
                  <ListChecks className="h-4 w-4 mr-2" /><span className="hidden sm:inline">Actions</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowShareModal(true)}
                  className="text-muted-foreground hover:text-foreground"
                  title="Share conversation"
                  data-testid="share-btn"
                >
                  <Share2 className="h-4 w-4 mr-2" /><span className="hidden sm:inline">Share</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={exportConversation}
                  className="text-muted-foreground hover:text-foreground"
                  title="Export conversation"
                  data-testid="export-btn"
                >
                  <Download className="h-4 w-4 mr-2" /><span className="hidden sm:inline">Export</span>
                </Button>
              </div>
            )}
            {messages.length > 0 && isTemporaryMode && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={exportConversation}
                className="text-muted-foreground hover:text-foreground"
                title="Export conversation"
                data-testid="export-btn"
              >
                <Download className="h-4 w-4 mr-2" /><span className="hidden sm:inline">Export</span>
              </Button>
            )}
          </div>
        </header>

        {/* Actions Panel (when visible) */}
        {showActionsPanel && currentConversationId && !isTemporaryMode && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
            <ActionsPanel
              sourceType="chat"
              sourceId={currentConversationId}
              onToggleCollapse={(collapsed) => {
                if (collapsed) setShowActionsPanel(false);
              }}
            />
          </div>
        )}

        {/* Messages area */}
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
            {((isTemporaryMode ? temporaryMessages : messages).length === 0) && !streaming ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                {isTemporaryMode ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                      <EyeOff className="h-8 w-8 text-amber-500" />
                    </div>
                    <h2 className="text-xl font-semibold mb-2">Temporary Chat</h2>
                    <p className="text-muted-foreground max-w-md mx-auto mb-4">
                      Messages in this chat will not be saved. Your memory and knowledge base are still available for context.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Start typing to begin...
                    </p>
                  </div>
                ) : (
                  <EmptyState
                    mode={mode}
                    setMode={setMode}
                    setInput={setInput}
                    textareaRef={textareaRef}
                  />
                )}
              </motion.div>
            ) : (
              <MessageList
                messages={isTemporaryMode ? temporaryMessages : messages}
                streaming={streaming}
                streamingContent={streamingContent}
                loading={loading}
                formatMessageContent={formatMessageContent}
                copyToClipboard={copyToClipboard}
                copiedId={copiedId}
                messagesEndRef={messagesEndRef}
              />
            )}
          </div>
        </ScrollArea>

        {/* Temporary Mode Banner */}
        <AnimatePresence>
          {isTemporaryMode && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="px-4 sm:px-6 pb-2">
              <div className="max-w-3xl mx-auto">
                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4" />
                    <span className="text-sm font-medium">Temporary Chat</span>
                    <span className="text-xs opacity-75">Messages not saved · Still uses your memory & knowledge</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={endTemporaryChat}
                    className="text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 h-7 text-xs"
                  >
                    End
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="px-4 sm:px-6 pb-2">
              <div className="max-w-3xl mx-auto">
                <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3 flex items-center justify-between">
                  <span className="text-sm">{error}</span>
                  <Button variant="ghost" size="sm" onClick={handleRetry} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                    <RefreshCw className="h-4 w-4 mr-1" /> Retry
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input area */}
        <MessageInput
          input={input}
          setInput={setInput}
          mode={mode}
          setMode={setMode}
          showModeSelector={showModeSelector}
          setShowModeSelector={setShowModeSelector}
          loading={loading}
          streaming={streaming}
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          recordingDuration={recordingDuration}
          uploadingAttachment={uploadingAttachment}
          currentConversationId={currentConversationId}
          memorySuggestions={isTemporaryMode ? [] : memorySuggestions}
          sendMessage={sendMessage}
          handleKeyDown={handleKeyDown}
          startRecording={startRecording}
          stopRecording={stopRecording}
          uploadChatAttachment={uploadChatAttachment}
          saveMemorySuggestion={saveMemorySuggestion}
          dismissMemorySuggestion={dismissMemorySuggestion}
        />
      </div>

      {/* Modals */}
      <KnowledgeBaseModal
        showDocuments={showDocuments}
        setShowDocuments={setShowDocuments}
        knowledgeDocs={knowledgeDocs}
        docsLoading={docsLoading}
        uploadingDoc={uploadingDoc}
        storageUsed={storageUsed}
        storageLimit={storageLimit}
        uploadKnowledgeDoc={uploadKnowledgeDoc}
        deleteKnowledgeDoc={deleteKnowledgeDoc}
      />

      <MemoriesModal
        showMemories={showMemories}
        setShowMemories={setShowMemories}
        memories={memories}
        memoriesLoading={memoriesLoading}
        newMemory={newMemory}
        setNewMemory={setNewMemory}
        newMemoryCategory={newMemoryCategory}
        setNewMemoryCategory={setNewMemoryCategory}
        addMemory={addMemory}
        deleteMemory={deleteMemory}
        updateMemory={updateMemory}
      />

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        conversationId={currentConversationId}
        conversationTitle={currentConversation?.title}
      />

      <ProfileModal
        showProfile={showProfile}
        setShowProfile={setShowProfile}
        user={user}
        onProfileUpdate={updateUser}
      />

      <DraftModal
        open={showDraftModal}
        onOpenChange={setShowDraftModal}
        sourceType="chat"
        sourceId={currentConversationId}
        onUseDraft={(draft) => {
          setInput(draft);
          textareaRef.current?.focus();
        }}
      />

      <FocusModal
        open={showFocus}
        onOpenChange={setShowFocus}
      />
    </div>
  );
};

export default ChatPage;
